import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";

const Login = ({ setUser }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      console.log("Login response:", response.data); // Debug logging
      
      localStorage.setItem("token", response.data.access_token);
      
      toast.success("Welcome back!");
      
      // Safely access user role with validation
      const userRole = response.data?.user?.role;
      if (!userRole) {
        console.error("User role not found in response:", response.data);
        throw new Error("User role not found in login response");
      }
      
      // Redirect directly to role-specific dashboard using full page reload
      window.location.href = `/dashboard/${userRole}`;
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || error.message || "Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-amber-50 to-red-100 p-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(220,38,38,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(251,191,36,0.1),transparent_50%)]" />
      
      {/* Back to Home Link */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 z-20 text-red-600 hover:text-red-700 font-medium flex items-center gap-2 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-red-100">
        <CardHeader className="space-y-4 text-center">
          <Link to="/" className="mx-auto w-32 h-32 flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
            <img 
              src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" 
              alt="PlayBack Token Logo" 
              className="w-full h-full object-contain"
            />
          </Link>
          <div>
            <CardTitle className="text-3xl font-bold text-red-600 brand-font">PlayBack Token</CardTitle>
            <CardDescription className="text-base mt-2">Log your volunteer hours and make an impact</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="border-red-200 focus:border-red-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="border-red-200 focus:border-red-400"
              />
            </div>
            <Button 
              type="submit" 
              data-testid="login-button"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-base font-medium rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-500">
              <strong>New to PlayBack Token?</strong>
            </p>
            <p className="text-center text-xs text-gray-500 mt-2">
              Volunteers must use the signup link provided by their club supervisor.
            </p>
            <p className="text-center text-xs text-gray-500 mt-1">
              Companies must contact admin to create an account.
            </p>
            
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
              <p className="text-center text-xs text-gray-600">
                <a 
                  href="/marketing/index.html" 
                  className="text-red-600 hover:text-red-700 underline font-medium"
                >
                  Click here for more information
                </a>
              </p>
              <p className="text-center text-xs text-gray-600">
                Contact us:{" "}
                <a 
                  href="mailto:info@playbacktoken.ie" 
                  className="text-red-600 hover:text-red-700 underline"
                >
                  info@playbacktoken.ie
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
